/**
 * Yarnly Crochet — faq.js
 *
 * <yarnly-faq data-allow-multiple="true|false" data-section-id> enhances the
 * native accordion in sections/faq.liquid:
 *   <details class="disclosure" data-faq-item data-block-id> <summary>…</summary> … </details>
 *
 *   - allow multiple false: opening one item closes the others (the `toggle`
 *     event covers clicks, keyboard, find-in-page and editor opens alike);
 *   - Escape inside an open item closes it and returns focus to its summary;
 *   - the theme editor opens the item of the selected block.
 * Without JavaScript every <details> still opens and closes on its own.
 *
 * Plain ES2020 IIFE, safe to evaluate twice (the define is guarded).
 */
(function () {
  'use strict';

  if (!window.Yarnly) return;

  if (!customElements.get('yarnly-faq')) {
    class YarnlyFaq extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
      }

      connectedCallback() {
        // `toggle` does not bubble, so listen in the capture phase.
        this.addEventListener('toggle', this.onToggle, true);
        this.addEventListener('keydown', this.onKeyDown);
        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('toggle', this.onToggle, true);
        this.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      get allowMultiple() {
        return this.getAttribute('data-allow-multiple') !== 'false';
      }

      get items() {
        return Array.from(this.querySelectorAll('details[data-faq-item]'));
      }

      itemFrom(node) {
        const details = node && typeof node.closest === 'function' ? node.closest('details[data-faq-item]') : null;
        return details && this.contains(details) ? details : null;
      }

      closeOthers(current) {
        this.items.forEach((item) => {
          if (item !== current && item.open) item.open = false;
        });
      }

      onToggle(event) {
        const details = event.target;
        if (!details || typeof details.matches !== 'function' || !details.matches('details[data-faq-item]')) return;
        if (!details.open || this.allowMultiple) return;
        this.closeOthers(details);
      }

      onKeyDown(event) {
        if (event.key !== 'Escape' && event.key !== 'Esc') return;
        const details = this.itemFrom(event.target);
        if (!details || !details.open) return;
        event.preventDefault();
        details.open = false;
        const summary = details.querySelector('summary');
        if (summary) summary.focus();
      }

      onBlockSelect(event) {
        const blockId = event.detail && event.detail.blockId;
        if (!blockId) return;
        const target = this.items.find((item) => item.getAttribute('data-block-id') === blockId);
        if (!target) return;
        if (!this.allowMultiple) this.closeOthers(target);
        target.open = true;
      }
    }

    customElements.define('yarnly-faq', YarnlyFaq);
  }
})();
